import { History, RemoveRedEye } from '@mui/icons-material';
import {
  EditAppBar,
  useDialog,
  useConcurrentialAccess as usePrismaConcurrentialAccess,
} from '@prismamedia/one-components';
import type { FC } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { CategoryStatus } from '../../../../../__generated__/queries-web';
import { Skeletons } from '../../../../../components/Skeletons';
import { paths } from '../../../../../routing/Router/paths';
import { auth } from '../../../../../utils/auth';
import { CategoriesContext } from '../../../categories.context';
import { FormContext } from '../../../form.context';
import { Category, PageRouteParams } from '../../../types';
import { isPageEditView, navigateTo } from '../../../utils';
import { useCategoryConcurrentialAccess } from '../../../utils/categoryConcurrentialAccess';
import { PageHistory } from './components/PageHistory';
import { StatusMenu } from './components/StatusMenu';
import { Title } from './components/Title';
import {
  STATUS_MENU_PLACEHOLDER_HEIGHT,
  STATUS_MENU_PLACEHOLDER_WIDTH,
} from './constants';
import { useStyles } from './styles';

export const PageEditAppBar: FC = () => {
  const classes = useStyles();
  const {
    activeCategory,
    isCategoriesLoading,
    refetchCategories,
    categories,
  } = useContext(CategoriesContext);
  const {
    form,
    handlers,
    isFormLoading,
    isFormInvalid,
    isFormPristine,
    errors,
  } = useContext(FormContext);

  const { closeDialog, openDialog } = useDialog();
  const routeParams = useParams<PageRouteParams>();
  const history = useHistory();
  const {
    lockCategory,
    unlockCategory,
    retainCategoryLock,
  } = useCategoryConcurrentialAccess();

  const searchParams = useMemo(
    () => new URLSearchParams(history.location.search),
    [history.location.search],
  );

  const actions = useMemo(
    () => [
      {
        disabled: !activeCategory?.historyCount,
        id: 'history',
        label: 'Historique',
        icon: <History />,
        onClick: () =>
          openDialog(
            <PageHistory
              activeCategory={activeCategory}
              onCloseHistory={() => {
                closeDialog();
              }}
            />,
            { fullWidth: true },
          ),
      },
      {
        disabled: true,
        icon: <RemoveRedEye />,
        id: 'see-front',
        label: 'Visualiser',
        onClick: () => {},
      },
    ],
    [activeCategory, closeDialog, openDialog],
  );

  /**
   * We pushing back into the view list
   */
  const handleNavigationBack = useCallback(() => {
    if (activeCategory) {
      // When lockedBy === null is the case an user go to an unlocked article and lock it
      if (
        auth.user?.id === activeCategory.lockedBy?.id ||
        activeCategory.lockedBy === null
      ) {
        unlockCategory(activeCategory.id).catch(() => {});
      }

      // Check if we came from breadcrumbs
      const fromBreadcrumbs = searchParams.get('from') === 'Breadcrumbs';
      const categoryId = searchParams.get('categoryId');

      // Determine the target category:
      // 1. If from breadcrumbs and it's the current category, go back to its view
      // 2. If it's a level 2 category, go back to its view
      // 3. Otherwise, go back to its parent
      const targetCategory = categories.find(
        (cat) =>
          cat.id ===
          (fromBreadcrumbs && categoryId === activeCategory.id
            ? activeCategory.id
            : activeCategory.level === 2
            ? activeCategory.id
            : activeCategory.parent?.id),
      );

      const pathname = `${generatePath(paths.PAGE_LIST, {
        brandKey: routeParams.brandKey,
        format: routeParams.format,
      })}${targetCategory ? `/${targetCategory.id}` : ''}`;

      navigateTo({
        pathname,
        opts: {
          method: 'REPLACE',
          search: searchParams.toString(),
        },
      });
    }
  }, [activeCategory, categories, routeParams, searchParams, unlockCategory]);

  // Need this hack to disable concurrentialAccess while the context set the correct activeCategory
  // TODO refacto this shit -> CategoriesContext
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!ready) {
      setReady(true);
    }
  }, [activeCategory, ready]);

  const onLock = useCallback(
    (firstLock) => {
      if (activeCategory && activeCategory.id) {
        if (firstLock) {
          return lockCategory(activeCategory.id);
        }
        return retainCategoryLock(activeCategory.id);
      }
      return Promise.resolve();
    },
    [activeCategory, lockCategory, retainCategoryLock],
  );

  usePrismaConcurrentialAccess({
    currentUser: auth.user,
    lockStatus: ready ? activeCategory : null,
    unlockTimeout: 600000,
    onLock,
    onNavigateBack: handleNavigationBack,
    disableLockForV0: true,
  });

  return (
    <EditAppBar
      actions={actions}
      additionalElement={
        isCategoriesLoading && isPageEditView() ? (
          <Skeletons
            height={STATUS_MENU_PLACEHOLDER_HEIGHT}
            width={STATUS_MENU_PLACEHOLDER_WIDTH}
          />
        ) : (
          <StatusMenu
            status={form.status}
            onStatusChange={(newStatus: CategoryStatus) => {
              handlers.handleFormChange({
                path: ['status'],
                value: newStatus,
              });
            }}
          />
        )
      }
      className={classes.appBar}
      {...(isPageEditView() && {
        concurrentialAccess: {
          lockStatus: { lockedBy: activeCategory?.lockedBy || auth.user },
          disableLockForV0: true,
        },
      })}
      navigationBackDisabled={!activeCategory}
      title={<Title />}
      onNavigateBack={handleNavigationBack}
      save={{
        disabled:
          isCategoriesLoading ||
          isFormInvalid ||
          isFormPristine ||
          (form.slug ? errors.slug?.hasError : false),
        loading: isFormLoading,
        onClick: () => {
          handlers.handleFormSubmit(
            form,
            (newCategory?: Category) => {
              // Form reinitialization
              handlers.setForm(form, true);

              // Only redirect if it's a new category creation
              if (
                newCategory &&
                newCategory.updatedAt === newCategory.createdAt
              ) {
                refetchCategories();

                // Check if we came from "Ajouter un élément"
                const fromSpeedDial = searchParams.get('from') === 'SpeedDial';

                // Only update active category if NOT from speed dial and we have a parent
                if (!fromSpeedDial && newCategory.parent?.id) {
                  // Keep current active category
                }

                // We redirect to the edit view of the new category
                navigateTo({
                  pathname: `${generatePath(paths.PAGE_EDIT, routeParams)}/${
                    newCategory.id
                  }`,
                  opts: { method: 'REPLACE' },
                });
              }
            },
            { skipNotification: false },
          );
        },
        requiredFields: null,
      }}
    />
  );
};
